// Full-screen overlay shown when an incoming friend challenge arrives.
// Auto-declines and closes when the countdown reaches zero.
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { S } from '../locales/strings.js'

export interface ChallengeModalProps {
  challengeId: string
  fromName: string
  /** Unix timestamp in milliseconds when the challenge expires. */
  expiresAt: number
  onAccept: (challengeId: string) => void
  onDecline: (challengeId: string) => void
}

export const ChallengeModal: React.FC<ChallengeModalProps> = ({
  challengeId,
  fromName,
  expiresAt,
  onAccept,
  onDecline,
}) => {
  const { t } = useTranslation()
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.round((expiresAt - Date.now()) / 1000))
  )

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.round((expiresAt - Date.now()) / 1000))
      setSecondsLeft(remaining)
      if (remaining === 0) {
        clearInterval(interval)
        onDecline(challengeId)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [challengeId, expiresAt, onDecline])

  const isUrgent = secondsLeft < 10

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.icon}>⚔</div>
        <h2 style={styles.title}>{t(S.CHALLENGE_MODAL_TITLE)}</h2>
        <p style={styles.from}>
          {t(S.CHALLENGE_MODAL_FROM, { name: fromName })}
        </p>
        <p style={{ ...styles.countdown, color: isUrgent ? '#e57373' : '#9B6FE8' }}>
          {t(S.CHALLENGE_MODAL_EXPIRES, { seconds: secondsLeft })}
        </p>
        <div style={styles.buttonRow}>
          <button
            style={styles.acceptButton}
            onClick={() => onAccept(challengeId)}
          >
            {t(S.CHALLENGE_MODAL_ACCEPT)}
          </button>
          <button
            style={styles.declineButton}
            onClick={() => onDecline(challengeId)}
          >
            {t(S.CHALLENGE_MODAL_DECLINE)}
          </button>
        </div>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
    pointerEvents: 'all',
  },
  card: {
    background: 'rgba(20,14,36,0.95)',
    border: '1px solid #2a1f42',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    maxWidth: 340,
    padding: '32px 28px',
    width: '90vw',
    boxSizing: 'border-box',
  },
  icon: {
    fontSize: 40,
    lineHeight: 1,
  },
  title: {
    color: '#c8a84b',
    fontFamily: 'monospace',
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: 1,
    margin: 0,
    textAlign: 'center',
  },
  from: {
    color: '#eee',
    fontFamily: 'monospace',
    fontSize: 15,
    margin: 0,
    textAlign: 'center',
  },
  countdown: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 700,
    margin: 0,
    transition: 'color 0.3s',
  },
  buttonRow: {
    display: 'flex',
    gap: 10,
    marginTop: 8,
    width: '100%',
  },
  acceptButton: {
    background: 'linear-gradient(135deg, #9B6FE8 0%, #7a50d0 100%)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    cursor: 'pointer',
    flex: 1,
    fontFamily: 'monospace',
    fontSize: 15,
    fontWeight: 700,
    minHeight: 44,
    padding: '0 16px',
    transition: 'opacity 0.15s',
  },
  declineButton: {
    background: 'transparent',
    border: '1px solid #3a3050',
    borderRadius: 8,
    color: '#9B6FE8',
    cursor: 'pointer',
    flex: 1,
    fontFamily: 'monospace',
    fontSize: 15,
    fontWeight: 700,
    minHeight: 44,
    padding: '0 16px',
    transition: 'opacity 0.15s',
  },
}
